import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { AuthContextProps } from 'react-oidc-context';
import type { SignoutRedirectArgs } from 'oidc-client-ts';
import { AxiosError } from 'axios';
import { GupUserData, Phone, User } from '../models/user';
import { store } from './store';
import agent from '../api/agent';
import { PersonalDataValues } from '../models/personalDataValues';
import { PreferencesDataValues } from '../models/preferencesDataValues';
import { AddressDataValues } from '../models/addressDataValues';
import { currencySymbolsMap } from '../constants/currency';
import { translate } from '../../i18n';
import { IOptionsPages } from '../models/paginationOptions';
import { buildQueryParams, generateLocaleRoute } from '../utils/uri/uri.utils';
import { Manual } from '../models/manual';
import { Measure } from '../utils/parcel-measurements.ts/parcel-measurements-utils';
import { DEFAULT_COUNTRY } from './localizationsStore';
import { Modals } from '../constants/modals';
import { handleError } from '../utils/generic/generic.utils';
import { Recipient } from '../models/parcelDeliveryMiles';
import { isFeatureRequired } from '../utils/client/client.utils';
import { AppRoute } from '../constants/route';
import { router } from '../..';

export type SocialLoginType = 'apple' | 'google';

interface LogoutProps {
  omitApiLogout?: boolean;
  interruptLogoutAfterRedirect?: boolean;
}

interface LoginGupProps {
  auth: AuthContextProps;
  redirectUri?: string;
  isGuestFlow?: boolean;
}

export default class UserStore {
  user: User | null | undefined = null;

  senderAddresses: AddressDataValues[] = [];

  gupUserData: GupUserData | undefined = undefined;

  receivers: Recipient[] | undefined = [];

  isLoadingUser: boolean = false;

  isLoadingReceivers: boolean = false;

  googleSignInProgress: boolean = false;

  appleSignInProgress: boolean = false;

  emailSignInProgress: boolean = false;

  signUpInProgress: boolean = false;

  passwordResetProgress: boolean = false;

  emailVerificationProgress: boolean = false;

  isLogoutRequested: boolean = false;

  isOnboardingManualLoading: boolean = false;

  onboardingManual: Manual | undefined = undefined;

  isNeedMigrationAgreement: boolean = false;

  receiversOptions: IOptionsPages = {
    page: 1,
    totalPagesCount: 1,
    perPage: 12,
    totalElCount: 3,
  };

  isMigratingInProgress: boolean = false;

  isDeletingAccount: boolean = false;

  isRequestingPhoneCode: boolean = false;

  isPhoneVerifying: boolean = false;

  isLoadingAddresses: boolean = false;

  addressesInUpdateState: number[] = [];

  private signoutRedirect:
    | null
    | ((args: SignoutRedirectArgs | undefined) => Promise<void>) = null;

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'UserStore',
      properties: ['receivers', 'receiversOptions'],
      storage: window.localStorage,
    });
    makePersistable(this, {
      name: 'UserStoreSession',
      properties: ['isLogoutRequested'],
      storage: window.sessionStorage,
    });
  }

  get urlSearchParams() {
    const searchParams = new URLSearchParams(window.location.search);
    const params = searchParams ? `?${searchParams}` : '';
    return params;
  }

  get measures() {
    return (
      store.userStore.user?.measures ??
      store.localizationsStore.selectedCountry?.default_measures ??
      Measure.KG_CM
    );
  }

  loginGup = async ({ auth, redirectUri, isGuestFlow }: LoginGupProps) => {
    if (isGuestFlow) {
      store.commonStore.resetAppState();
      store.navStore.setServicesSidebarVisibility(false);
    }

    return auth.signinRedirect({
      state: { redirect: redirectUri },
      extraQueryParams: {
        lang: store.localizationsStore.selectedLocalization?.code ?? '',
        country:
          store.localizationsStore.selectedCountry?.alias?.toLocaleUpperCase() ??
          '',
      },
    });
  };

  logout = async (
    { omitApiLogout, interruptLogoutAfterRedirect }: LogoutProps = {
      omitApiLogout: false,
      interruptLogoutAfterRedirect: false,
    }
  ) => {
    this.setIsLogoutRequested(true);

    // Required to intercept logout behaviour (e.g. with modal dialog during parcel creation flow)
    if (interruptLogoutAfterRedirect) {
      // It is ugly but I haven't found another way to trigger logout interception via navigation-blocker.
      // (Ugly because redirect functionality is duplicated in oauth-redirect.component.tsx)
      const newRoute = isFeatureRequired('hasMarketingPage')
        ? generateLocaleRoute()
        : AppRoute.ROOT;

      router.navigate(newRoute, { replace: true });
      return;
    }

    if (!omitApiLogout && this.signoutRedirect) {
      runInAction(() => {
        store.commonStore.setAppLoaded(false);
      });

      try {
        store.commonStore.analyticsManager.track('logout', {
          user_id: this.user?.id,
        });
        await this.signoutRedirect(undefined);
      } catch (error) {
        handleError(error);
      } finally {
        runInAction(() => {
          store.commonStore.setAppLoaded(true);
        });
      }
    }

    if (omitApiLogout) {
      runInAction(() => {
        store.commonStore.resetAppState();
        this.setIsLogoutRequested(false);
      });

      if (isFeatureRequired('hasMarketingPage')) {
        router.navigate(generateLocaleRoute(), { replace: true });
      } else {
        window.location.replace(store.commonStore.landingPageUrl);
      }
    }
  };

  setSignoutRedirect = (fn: (args: any | undefined) => Promise<void>) => {
    this.signoutRedirect = fn;
  };

  getCurrencySymbol = (currencyCode?: string) => {
    if (currencyCode) return currencySymbolsMap[currencyCode];
    if (this.user) return currencySymbolsMap[this.user!.currency];
    const selectedCountryCurrency =
      store.localizationsStore.selectedCountry?.default_currency;
    if (selectedCountryCurrency)
      return currencySymbolsMap[selectedCountryCurrency];
    return currencySymbolsMap[DEFAULT_COUNTRY.default_currency];
  };

  getInitials = () => {
    let initials = '';

    if (this.user?.first_name && this.user?.last_name) {
      initials =
        this.user!.first_name.charAt(0) + this.user!.last_name.charAt(0);
    } else {
      initials = this.user!.email?.slice(0, 2).toLocaleUpperCase();
    }

    return initials;
  };

  getAddress = () => {
    let address: string = '';

    if (this.user?.address?.building && this.user?.address?.building !== '')
      address += this.user!.address!.building;
    if (this.user?.address?.street && this.user?.address?.building !== '')
      address += (address !== '' ? ' ' : '') + this.user!.address!.street;
    if (this.user?.address?.apartment && this.user?.address?.building !== '')
      address +=
        (address !== '' ? `, ${translate('apt')}. ` : `${translate('apt')}. `) +
        this.user!.address!.apartment;

    return address !== '' ? address : null;
  };

  getUserName = (includeMiddleName: boolean = false) => {
    const fullName: (string | undefined | null)[] = [];

    if (!this.user) return '';

    fullName.push(this.user?.first_name);
    if (includeMiddleName) fullName.push(this.user?.middle_name);
    fullName.push(this.user?.last_name);

    return fullName.filter((x) => x).join(' ');
  };

  setLoginData = ({
    accessToken,
    refreshToken,
  }: {
    accessToken: string;
    refreshToken: string;
  }) => {
    runInAction(() => {
      if (store.commonStore.isGuestParcelCreation) {
        store.commonStore.setIsGuestParcelCreation(false);
        store.parcelCreationStore.setIsParcelCreationInProgress(false);
      }
      store.commonStore.setAccessToken(accessToken);
      store.commonStore.setRefreshToken(refreshToken);
    });
  };

  changePassword = async (newPassword: string, oldPassword?: string) => {
    this.isLoadingUser = true;

    try {
      const { user, gup } = await agent.ProfileV2.updatePassword(
        newPassword,
        oldPassword
      );
      this.user = user;
      this.gupUserData = gup;
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.isLoadingUser = false;
      });
    }
  };

  getCurrentUser = async () => {
    runInAction(() => {
      this.isLoadingUser = true;
    });
    if (!this.user) store.commonStore.setAppLoaded(false);
    try {
      const { user, gup } = await agent.ProfileV2.profile();
      if (!user) return;
      runInAction(() => {
        this.user = user;
        this.gupUserData = gup;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingUser = false;
        store.commonStore.setAppLoaded(true);
      });
    }
  };

  getCurrentUserGuestMode = async () => {
    runInAction(() => {
      this.isLoadingUser = true;
    });
    if (!this.user) store.commonStore.setAppLoaded(false);
    try {
      const user = await agent.Profile.profile();
      if (!user) return;
      runInAction(() => {
        this.user = user;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingUser = false;
        store.commonStore.setAppLoaded(true);
      });
    }
  };

  setUser = (user: User | null) => {
    this.user = user;
  };

  setPhoneForVerification = (phone: Phone | undefined) => {
    if (this.user) {
      this.user.phoneForVerification = phone;
    }
  };

  updatePhone = async (phone: Phone) => {
    this.isLoadingUser = true;
    this.isRequestingPhoneCode = true;

    const result: {
      requiresVerification: boolean;
      error?: string;
    } = {
      requiresVerification: false,
      error: undefined,
    };

    try {
      const res = await agent.ProfileV2.updatePhone(
        phone.phone_number,
        phone.country_code
      );

      if (!res.success) {
        result.error = res.message;
        return result;
      }

      if (res.data?.requires_verification) {
        result.requiresVerification = true;
        return result;
      }

      if (this.user) {
        this.user.phone = phone;
        this.user.phone_verified = true;
      }

      return result;
    } catch (error) {
      if (error instanceof AxiosError) {
        result.error = error.response?.data.message;
      } else {
        handleError(error);
      }

      return result;
    } finally {
      runInAction(() => {
        this.isRequestingPhoneCode = false;
        this.isLoadingUser = false;
      });
    }
  };

  // Updates first_name, last_name and middle_name for currently logged in user.
  updatePersonalData = async (personalData: PersonalDataValues) => {
    if (!this.user) return false;

    this.isLoadingUser = true;
    try {
      const updatedUser = await agent.Profile.updatePersonalData(personalData);

      runInAction(() => {
        this.user! = updatedUser!;
      });
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.isLoadingUser = false;
      });
    }
  };

  // Updates user localization settings
  updatePreferences = async (preferences: PreferencesDataValues) => {
    if (!this.user) return false;
    try {
      const updatedUser = await agent.Profile.updatePreferences(preferences);

      runInAction(() => {
        this.user! = updatedUser!;
      });

      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  updateAddress = async (addressInfo: AddressDataValues) => {
    if (!this.user) return false;

    this.isLoadingUser = true;
    try {
      const updatedUser = await agent.Profile.updateAddress(addressInfo);

      runInAction(() => {
        this.user! = updatedUser!;
      });
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.isLoadingUser = false;
      });
    }
  };

  deleteAccount = () => {
    if (!this.user) return;
    this.isDeletingAccount = true;
    store.modalStore.update(Modals.DELETE_ACCOUNT, { isLoading: true });

    try {
      agent.ProfileV2.deleteProfile().then(() => {
        store.commonStore.analyticsManager.track('delete_account', {
          user_id: this.user?.id,
        });
        // Redirect to Main Page/Parcel List on delete account
        this.logout();
        store.modalStore.close(Modals.DELETE_ACCOUNT);
        this.isDeletingAccount = false;
      });
    } catch (error) {
      console.error(error);
    }
  };

  getReceivers = async (
    options: IOptionsPages = {
      page: 1,
      totalPagesCount: 1,
      perPage: 12,
      totalElCount: 3,
    },
    append: boolean = false
  ) => {
    this.isLoadingReceivers = true;
    const query = buildQueryParams(options);

    try {
      const { data, meta } = await agent.Profile.receivers(query);

      runInAction(() => {
        const existingReceivers = this.receivers || [];

        if (
          append &&
          options.page &&
          options.page > 1 &&
          existingReceivers.length > 0
        ) {
          this.receivers = [...existingReceivers, ...data!];
        } else {
          this.receivers = data;
        }

        this.receiversOptions = { ...options };
        this.receiversOptions.totalPagesCount = meta?.pageCount;
        this.receiversOptions.totalElCount = meta?.totalCount;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingReceivers = false;
      });
    }
  };

  setReceiversOptions = (options: IOptionsPages) => {
    this.receiversOptions = { ...this.receiversOptions, ...options };
  };

  incrementPerPage = (perPage: number) => {
    this.receiversOptions.perPage = perPage + 12;
  };

  setIsLogoutRequested = (isRequested: boolean) => {
    this.isLogoutRequested = isRequested;
  };

  getOnboardingManual = async () => {
    store.modalStore.open({
      id: Modals.ONBOARDING_MANUAL,
      name: Modals.ONBOARDING_MANUAL,
    });
    this.isOnboardingManualLoading = true;
    try {
      const data = await agent.Shipment.onboarding();
      runInAction(() => {
        this.onboardingManual = data;
        this.isOnboardingManualLoading = false;
      });
    } catch (error) {
      handleError(error);
    }
  };

  confirmOnboardingManual = async () => {
    try {
      const data = await agent.User.confirmOnboardingManual();
      if (data?.user) {
        runInAction(() => {
          this.user = data.user;
        });
      }
    } catch (error) {
      handleError(error);
    }
  };

  migrateUser = async (shouldMigrate: boolean) => {
    this.isMigratingInProgress = true;
    try {
      await agent.Profile.migrate(shouldMigrate);
      if (shouldMigrate) {
        await this.getCurrentUser();
      }

      runInAction(() => {
        this.isNeedMigrationAgreement = false;
      });
    } catch (error) {
      handleError(error);
    } finally {
      this.isMigratingInProgress = false;
    }
  };

  verifyPhone = async (
    phoneNumber: string,
    phoneCountryCode: string,
    code: string
  ) => {
    this.isPhoneVerifying = true;
    try {
      const phoneCountryCodeUppercase = phoneCountryCode.toUpperCase();
      await agent.ProfileV2.verifyPhone(
        phoneCountryCodeUppercase,
        phoneNumber,
        code
      );

      store.commonStore.analyticsManager.track(
        'confirm_phone_number_verification'
      );

      runInAction(() => {
        // clear verification phone when main phone is approved
        this.setPhoneForVerification(undefined);
        if (this.user) {
          this.user.phone = {
            country_code: phoneCountryCodeUppercase,
            phone_number: phoneNumber,
          };
          this.user.phone_verified = true;
        }
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      runInAction(() => {
        this.isPhoneVerifying = false;
      });
    }
  };

  getAddresses = async () => {
    this.isLoadingAddresses = true;

    try {
      const addresses = await agent.Profile.getSenderAddresses();
      if (addresses) {
        runInAction(() => {
          this.senderAddresses = addresses;
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingAddresses = false;
      });
    }
  };

  private normalizeDefaultAddresses = (address: AddressDataValues) => {
    this.senderAddresses
      .filter((x) => x.country === address.country)
      .forEach((x) => {
        x.is_default = false;
      });
  };

  createAddress = async (address: AddressDataValues) => {
    this.isLoadingAddresses = true;

    try {
      const createAddress = await agent.Profile.createSenderAddress(address);
      if (createAddress) {
        runInAction(() => {
          if (createAddress.is_default) {
            this.normalizeDefaultAddresses(createAddress);
          }
          const newAddresses = [...this.senderAddresses, createAddress];
          this.senderAddresses = newAddresses;
        });
      }
      return createAddress;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingAddresses = false;
      });
    }
  };

  updateSenderAddress = async (address: AddressDataValues) => {
    this.addressesInUpdateState = [...this.addressesInUpdateState, address.id];

    try {
      const updatedAddress = await agent.Profile.updateSenderAddress(address);
      if (updatedAddress) {
        runInAction(() => {
          if (updatedAddress.is_default) {
            this.normalizeDefaultAddresses(updatedAddress);
          }
          const newAddresses = [
            ...this.senderAddresses.filter((x) => x.id !== updatedAddress.id),
            updatedAddress,
          ];
          this.senderAddresses = newAddresses;
        });
      }
      return updatedAddress;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.addressesInUpdateState = [
          ...this.addressesInUpdateState.filter((x) => x !== address.id),
        ];
      });
    }
  };

  deleteSenderAddress = async (id: number) => {
    this.addressesInUpdateState = [...this.addressesInUpdateState, id];

    try {
      await agent.Profile.deleteSenderAddress(id);
      runInAction(() => {
        this.senderAddresses = [
          ...this.senderAddresses.filter((x) => x.id !== id),
        ];
      });
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.addressesInUpdateState = [
          ...this.addressesInUpdateState.filter((x) => x !== id),
        ];
      });
    }
  };
}
